import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IModule } from "@formant/core/model/IModule";

import { customModuleStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class Modules extends Component {
    @localSettingObservable("settings-adapters-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-adapters-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;

        const columns: IDataTableColumn<IModule>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="utility" />,
                sortBy: _ => _.name,
            },
            {
                header: "Description",
                row: _ => _.description,
                sortBy: _ => _.description || "",
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "lg",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "lg",
            },
            {
                header: "",
                row: _ => <Actions customModule={_} />,
            },
        ];

        const customModules = (customModuleStore.listAll() || []).filter(
            _ => !_.id?.startsWith("formant:"),
        );
        const { filter, onChangeFilter, sort, onChangeSort } = this;

        const dimensions: {
            dimensionKey: string;
            dimensionName: string;
            getValue: (_: IModule) => any;
        }[] = [];

        const filtered = applySimpleFilter(
            customModules || [],
            dimensions,
            _ => _.name,
            filter,
        );

        return (
            <SettingsPage
                title="Modules"
                extra={
                    <Help text="A custom module is a module for views that is controlled by a website you own." />
                }
                actions={
                    <StandardButton to={routeTo.createCustomModule()}>
                        Add module
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="utility"
                    loading={!customModules}
                    totalQuantity={customModules?.length || 0}
                    totalLabel="Modules"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={customModules || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filtered || []}
                        columns={columns}
                        loading={customModules === undefined}
                        noResultsText="No custom modules found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (customModule: IModule) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editCustomModule(
                { id: defined(customModule.id) },
                { tab: "Settings" },
            ),
        );
    };
}
